import ApiClient from "@/lib/api-client"
import { sessionIdentity } from "@/sdk/transport/identity"

// Short enough that a reused response can't be perceived as stale.
const READ_TTL_MS = 1000

// A member bouncing between a list and a detail page is back within seconds.
const REVALIDATE_AFTER_MS = 30000

const THROTTLE_BACKOFF_MS = 5000
const MAX_THROTTLE_BACKOFF_MS = 60000

const READ_SCOPES = new Set(["member", "client"])

// The kept-alive working set: App.vue holds 6 pages, and a heavy one runs ~30
// cached reads once its queries, path resolves and cursor pages are counted.
const MAX_ENTRIES = 180

type Entry = {
  inFlight: Promise<any> | null
  value: any
  hasValue: boolean
  fetchedAt: number
}

export const stableStringify = (value: any): string => {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value) ?? "null"
  }

  // Without this the object branch collapses every date onto one key.
  if (value instanceof Date) return JSON.stringify(value)

  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`

  return `{${Object.keys(value)
    .sort()
    .map(key => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
    .join(",")}}`
}

// Authored code owns what it is handed, so each caller gets its own copy.
const clone = (value: any) =>
  value === null || typeof value !== "object"
    ? value
    : JSON.parse(JSON.stringify(value))

const throttledError = (): Error => {
  const error: any = new Error("Too many requests")

  error.response = { status: 429 }

  return error
}

export class ReadCache {
  private readonly reads = new Map<string, Entry>()
  private identity: string | null = null
  private throttledUntil = 0
  private throttleBackoff = THROTTLE_BACKOFF_MS
  private generation = 0

  get(
    endpoint: string,
    params: Record<string, any>,
    stale = false
  ): Promise<any> {
    this.syncIdentity()

    const key = `${endpoint}|${stableStringify(params)}`
    const entry = this.reads.get(key)

    if (entry) {
      this.touch(key, entry)
      const age = Date.now() - entry.fetchedAt

      if (entry.hasValue && (stale || age < READ_TTL_MS)) {
        // Past the window a serve buys the next read's freshness, not its own.
        if (
          stale &&
          age >= REVALIDATE_AFTER_MS &&
          !entry.inFlight &&
          !this.throttled
        ) {
          this.fetch(key, endpoint, params).catch(() => undefined)
        }

        return Promise.resolve(clone(entry.value))
      }

      if (entry.inFlight) return entry.inFlight.then(clone)
    }

    if (this.throttled) return Promise.reject(throttledError())

    return this.fetch(key, endpoint, params).then(clone)
  }

  async revalidating<T>(
    run: (stale: boolean) => Promise<T>,
    onUpdate?: (value: T) => void
  ): Promise<T> {
    const served = await run(true)

    // Only onUpdate needs a second read; a stale serve refreshes its own entry.
    if (!onUpdate) return served

    const before = stableStringify(served)
    const generation = this.generation

    run(false).then(
      fresh => {
        // A read that overlapped a write describes the state the write replaced.
        if (generation !== this.generation) return

        if (stableStringify(fresh) === before) return

        onUpdate(fresh)
      },
      () => undefined
    )

    return served
  }

  reset(): void {
    this.invalidate()
    this.identity = null
    this.throttledUntil = 0
    this.throttleBackoff = THROTTLE_BACKOFF_MS
  }

  // A read issued mid-write describes the state before it, so the clear on
  // settle is as load-bearing as the one up front.
  write(run: () => Promise<any>): Promise<any> {
    this.syncIdentity()
    this.invalidate()

    return this.track(run).finally(() => this.invalidate())
  }

  private fetch(
    key: string,
    endpoint: string,
    params: Record<string, any>
  ): Promise<any> {
    const generation = this.generation

    const promise = this.track(() =>
      ApiClient.get(endpoint, { params }).then((response: any) => response.data)
    )

    const entry = this.reads.get(key) ?? {
      inFlight: null,
      value: undefined,
      hasValue: false,
      fetchedAt: 0
    }

    entry.inFlight = promise
    this.touch(key, entry)
    this.evictOverflow()

    promise.then(
      value => this.settle(key, promise, value, generation),
      () => this.fail(key, promise)
    )

    return promise
  }

  private track<T>(run: () => Promise<T>): Promise<T> {
    return run()
      .then(result => {
        this.throttleBackoff = THROTTLE_BACKOFF_MS

        return result
      })
      .catch(error => {
        if (error?.response?.status === 429 && this.coversReads(error)) {
          this.openBreaker(error)
        }

        throw error
      })
  }

  private settle(
    key: string,
    promise: Promise<any>,
    value: any,
    generation: number
  ): void {
    const entry = this.reads.get(key)

    if (entry?.inFlight !== promise) return

    entry.inFlight = null

    if (generation !== this.generation) {
      this.reads.delete(key)

      return
    }

    entry.value = value
    entry.hasValue = true
    entry.fetchedAt = Date.now()
  }

  private fail(key: string, promise: Promise<any>): void {
    const entry = this.reads.get(key)

    if (entry?.inFlight !== promise) return

    entry.inFlight = null

    if (!entry.hasValue) this.reads.delete(key)
  }

  // Map order is insertion order, so re-setting a key makes it the newest and
  // the eviction below takes the least recently read.
  private touch(key: string, entry: Entry): void {
    this.reads.delete(key)
    this.reads.set(key, entry)
  }

  private evictOverflow(): void {
    while (this.reads.size > MAX_ENTRIES) {
      const oldest = this.reads.keys().next().value

      if (oldest === undefined) return

      this.reads.delete(oldest)
    }
  }

  private invalidate(): void {
    this.reads.clear()
    this.generation += 1
  }

  forget(endpoint: string): void {
    for (const key of Array.from(this.reads.keys())) {
      if (key.startsWith(`${endpoint}|`)) this.reads.delete(key)
    }
  }

  // Cached reads belong to the member and app that made them.
  private syncIdentity(): void {
    const identity = sessionIdentity()

    if (identity === this.identity) return

    this.identity = identity
    this.invalidate()
  }

  private get throttled(): boolean {
    return Date.now() < this.throttledUntil
  }

  private coversReads(error: any): boolean {
    const scope = error.response?.headers?.["x-ratelimit-scope"]

    return !scope || READ_SCOPES.has(scope)
  }

  private openBreaker(error: any): void {
    const retryAfter = Number(error.response?.headers?.["retry-after"]) * 1000
    const wait = retryAfter > 0 ? retryAfter : this.throttleBackoff

    this.throttledUntil = Date.now() + wait
    this.throttleBackoff = Math.min(
      this.throttleBackoff * 2,
      MAX_THROTTLE_BACKOFF_MS
    )
  }
}
